import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";

import { ModalId } from "../../../enums/modalId";
import { ToastMessages } from "../../../enums/toastMessages";
import { useFileToBase64 } from "../../../hooks/useFileToBase64";
import { cancelUploadTask } from "../../../redux/slice/fileUploadSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { AddMore } from "../../../svg/add-more";
import NewFileUploaderCard from "../../newFileUploader/newFileUploader";

interface DocumentsModalWithMergeProps {
  onClose: () => void;
  documentsName: string[];
  documentsList: File[];
  id: string;
  newDocumentNames?: (urls: string[]) => void;
  newFiles?: (files: File[]) => void;
  /**
   * File name to use for the merged PDF produced when `onMergePdfs` is
   * provided. The caller owns naming (typically patient-scoped, e.g.
   * `<first>_<last>_merged_documents.pdf`); the modal just forwards this
   * verbatim to {@link DocumentsModalWithMergeProps.onMergePdfs}. Defaults
   * to `merged-document.pdf` when omitted.
   */
  mergedFileName?: string;
  /**
   * Optional merge callback. When supplied, the modal converts every
   * uploaded file to base64 on Save and invokes this callback with the
   * base64 list and {@link DocumentsModalWithMergeProps.mergedFileName}
   * before closing.
   */
  onMergePdfs?: (base64Urls: string[], fileName?: string) => Promise<void>;
  isMerging?: boolean;
}

/**
 * Pharma-dashboard counterpart of medonc's `DocumentsModal` with PDF-merge
 * support. Lets the user attach one or more clinical PDFs and, when an
 * `onMergePdfs` callback is provided, merges them server-side into a single
 * patient-named PDF on Save. Mirrors the medonc UX but intentionally omits
 * the analytics (`apiWorkerManager.enqueueAndProcess`) hooks — pharma
 * doesn't have an equivalent BigQuery pipeline today.
 */
const DocumentsModalWithMerge = (props: DocumentsModalWithMergeProps) => {
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { progress } = useSelector((state: RootState) => state.fileUploading);
  const dispatch = useDispatch<AppDispatch>();

  /**
   * Lazy base64 conversion. We pass `autoConvert: false` so files are only
   * read into memory immediately before the merge call — avoids holding
   * large buffers while the user is still editing the attachment list.
   */
  const { convertFilesToBase64, clearBase64Urls } = useFileToBase64(
    undefined,
    false,
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    const duplicateFiles = newFiles.filter((newFile) =>
      uploadedFiles.some((existingFile) => existingFile.name === newFile.name),
    );

    if (duplicateFiles.length > 0) {
      toast.error(ToastMessages.DUPLICATE_FILE_UPLOAD);
      return;
    }

    const uniqueFiles = newFiles.filter(
      (newFile) =>
        !uploadedFiles.some(
          (existingFile) => existingFile.name === newFile.name,
        ),
    );

    setFileNames((prev) => [...prev, ...uniqueFiles.map((file) => file.name)]);
    setUploadedFiles((prev) => [...prev, ...uniqueFiles]);
  };

  /**
   * Persists the current attachment selection to the parent and, when an
   * `onMergePdfs` callback is provided, merges the uploaded files into a
   * single PDF before closing.
   *
   * Behavior:
   * - Always pushes the current `fileNames` and `uploadedFiles` to the
   *   parent via `props.newDocumentNames` / `props.newFiles`.
   * - If `props.onMergePdfs` is provided, converts every uploaded file to
   *   base64 and invokes the merge callback with
   *   `props.mergedFileName ?? "merged-document.pdf"`. This runs even for
   *   a single file so callers can normalize all attachments into one
   *   merged PDF.
   * - On merge failure, surfaces a toast and falls back to saving the
   *   individual files. Base64 buffers are released in both the success
   *   and failure paths via `clearBase64Urls`.
   * - Always closes the modal at the end.
   */
  const handleOnSave = async () => {
    props.newDocumentNames?.(fileNames);
    props.newFiles?.(uploadedFiles);

    if (props.onMergePdfs) {
      const mergedFileName = props.mergedFileName ?? "merged-document.pdf";

      try {
        const convertedBase64Urls = await convertFilesToBase64(uploadedFiles);
        await props.onMergePdfs(convertedBase64Urls, mergedFileName);
        clearBase64Urls();
      } catch (error) {
        console.error("Error merging PDFs:", error);
        toast.error(ToastMessages.MERGE_PDFS_ERROR);
        clearBase64Urls();
      }
    }

    props.onClose();
  };

  const handleClose = () => {
    dispatch(cancelUploadTask());
    props.onClose();
  };

  const handleFileDelete = (index: number) => {
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });

    setFileNames((prevFileNames) => {
      const newFileNames = [...prevFileNames];
      newFileNames.splice(index, 1);
      return newFileNames;
    });
  };

  useEffect(() => {
    setFileNames(props.documentsName.length > 0 ? props.documentsName : []);
    setUploadedFiles(props.documentsList);
  }, []);

  return (
    <Modal
      dialogId={ModalId.DOCUMENT_MERGE_MODAL}
      onSave={handleOnSave}
      title={"Attachments"}
      saveButtonText={props.isMerging ? "Merging PDFs..." : "Save"}
      cancelText={"Cancel"}
      onClose={handleClose}
      heightPercentage={70}
      disableSave={props.isMerging || false}
    >
      <div className="documents-modal--container flex flex-col gap-3">
        {fileNames.map((docName, index) => (
          <NewFileUploaderCard
            key={`${docName}-${index}`}
            fileName={docName}
            progress={progress}
            isUploading={progress !== 100 && progress > 0}
            handleDelete={() => handleFileDelete(index)}
          />
        ))}
        <div
          className="documents-modal--add-more mt-2 flex items-center gap-1 hover:cursor-pointer"
          onClick={handleClick}
        >
          <AddMore />
          <div className="documents-modal--add-more--text text-h12 font-semibold text-tertiaryBlue-4">
            Add More
          </div>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf"
          />
        </div>
      </div>
    </Modal>
  );
};

export default DocumentsModalWithMerge;
